import { RotateCcw, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';

import { Pill } from './pill';
import { formatCarmAngle } from '../../../common/utils';

import { cn } from '@/lib/utils';
import type { CarmView } from '@/visualization/domain/carm-view';

export interface CarmViewRowProps {
    view: CarmView;
    index: number;
    isSelected: boolean;
    isEdited: boolean;
    isCustom: boolean;
    onSelect: (index: number) => void;
    onRename: (index: number, name: string) => void;
    onDelete?: (index: number) => void;
    onReset?: (index: number) => void;
}

export function CarmViewRow({
    view,
    index,
    isSelected,
    isEdited,
    isCustom,
    onSelect,
    onRename,
    onDelete,
    onReset,
}: CarmViewRowProps) {
    const [isRenaming, setIsRenaming] = useState(false);
    const [editName, setEditName] = useState(view.name);
    const inputRef = useRef<HTMLInputElement>(null);

    const handleStartRename = (e: React.MouseEvent) => {
        e.stopPropagation();
        setEditName(view.name);
        setIsRenaming(true);
        // Focus after render
        setTimeout(() => inputRef.current?.select(), 0);
    };

    const handleCommitRename = () => {
        setIsRenaming(false);
        const trimmed = editName.trim();
        if (trimmed && trimmed !== view.name) {
            onRename(index, trimmed);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleCommitRename();
        } else if (e.key === 'Escape') {
            setIsRenaming(false);
        }
    };

    return (
        <div
            className={cn(
                'group flex cursor-pointer items-center gap-1 px-2 py-1 text-xs transition-colors duration-200 border-l-4',
                isSelected
                    ? 'border-(--group-color) text-foreground'
                    : 'border-transparent text-muted-foreground hover:bg-muted hover:text-foreground'
            )}
            onClick={() => !isRenaming && onSelect(index)}
        >
            {/* Delete button — custom views only */}
            {isCustom && onDelete && (
                <button
                    className="shrink-0 p-0.5 opacity-0 group-hover:opacity-100"
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete(index);
                    }}
                    title="Remove"
                >
                    <Trash2 className="h-3 w-3 transition-all duration-200 hover:text-destructive" />
                </button>
            )}

            {/* Reset button — edited pre-computed views only */}
            {!isCustom && isEdited && onReset && (
                <button
                    className="shrink-0 p-0.5 opacity-0 group-hover:opacity-100"
                    onClick={(e) => {
                        e.stopPropagation();
                        onReset(index);
                    }}
                    title="Reset to original"
                >
                    <RotateCcw className="h-3 w-3 transition-all duration-200 hover:text-primary" />
                </button>
            )}

            {/* Name — click to rename */}
            {isRenaming ? (
                <input
                    ref={inputRef}
                    className="min-w-0 flex-1 rounded border border-border bg-background px-1 text-foreground outline-none focus:border-primary"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onBlur={handleCommitRename}
                    onKeyDown={handleKeyDown}
                    onClick={(e) => e.stopPropagation()}
                />
            ) : (
                <span
                    className="min-w-0 flex-1 cursor-text truncate"
                    onDoubleClick={handleStartRename}
                    title="Double-click to rename"
                >
                    {view.name}
                </span>
            )}

            {/* Angle values */}
            <Pill className="font-mono tabular-nums">
                {formatCarmAngle(view.craniocaudal, 'craniocaudal')}
            </Pill>
            <Pill className="font-mono tabular-nums">{formatCarmAngle(view.oblique, 'oblique')}</Pill>
        </div>
    );
}
